import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import useSession from "../../Hooks/OpenTok/useSession"
import usePublisher from "../../Hooks/OpenTok/usePublisher"
import styled from "styled-components";
import useResizeAware from "react-resize-aware";
import Layout from "./Layout";
import PinTip from "./PinTip";
import CallButton from "./CallButton";
import CallButtonGroup from "./CallButtonGroup";
import DeviceSettingsDropdown from "./DeviceSettings";
import {useMedia} from "../../MediaStreamContext";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {light, regular, solid} from '@fortawesome/fontawesome-svg-core/import.macro';
import entranceChime from "../../../../audio/session/Muze-Intro-Noise.mp3"
import exitChime from "../../../../audio/session/Muze-Outro-Noise.mp3"
import '@opentok/client'

const RoomWrapper = styled.section`
    position: fixed;
    top: 0;
    left: 81px;
    bottom: 0;
    right: 0;
    z-index: 2;
    background: #4a4a4a;
    overflow: hidden;
    display: flex;
    gap: 0;
`

const ModalWrapper = styled.div`
    display: ${({open = false}) => open ? 'flex' : 'none'};
    position: fixed;
    top: 0;
    left: 81px;
    bottom: 0;
    right: 0;
    z-index: 99;
    background: rgba(0, 0, 0, 0.8);
    justify-content: center;
    align-items: center;
    transition: all 500ms ease-in;
`

const SettingsModalContainer = styled.div`
    background: #000;
    display: flex;
    flex-flow: column;
    align-items: stretch;
    justify-content: center;
    gap: 1em;
    padding: 4em;
    width: 50%;
    max-width: 650px;
    border: 1px solid #ffffff;
    border-radius: 10px;
`

const SettingsModalHeader = styled.h3`
    font-size: 18px;
    font-weight: 600;
    padding: 0;
    margin: 0;
    color: #ffffff;
`

const SettingsModalButtonWrapper = styled.div`
    display: flex;
    gap: 0.5em;
    justify-content: flex-end;
`

const SettingsModalButton = styled.button`
    position: relative;
    height: 50px;
    border-radius: 25px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    background: ${({active}) => active ? '#fff' : '#000'};
    color: ${({active}) => active ? '#333' : '#fff'};
    transition: all 150ms ease-in-out;
    border: 1px solid ${({active}) => active ? '#fff' : '#999'};
    flex: ${({fullwidth}) => fullwidth ? "1 0 100%" : "0 1 50%"};

    &:hover {
        background: #fff;
        border-color: #fff;
        color: #000;
    }
`

const SettingsModalDivider = styled.hr`
    width: 100%;
    flex: 1 0 100%;
    border-color: #999;
`

const SettingsModalButtonIcon = styled.div`
    position: absolute;
    left: 1em;
    top: 50%;
    transform: translateY(-50%);
`

const GateModalContainer = styled.div`
    background: #ffffff;
    display: flex;
    flex-flow: column;
    align-items: stretch;
    justify-content: center;
    gap: 1em;
    padding: 4em;
    width: 50%;
    max-width: 650px;
    border: 1px solid #ffffff;
    border-radius: 10px;
`

const GateModalHeading = styled.div`
    font-size: 18px;
    font-weight: 600;
    padding: 0;
    margin: 0;
`

const GateModalActionWrapper = styled.div`
    display: flex;
    gap: 2em;
    justify-content: center;
    align-items: center;
`

const GateModalAction = styled.button`
    background: ${({type}) => {
        switch (type){
            case 'primary':
                return '#5562c0'
            case 'outline':
                return '#ffffff'
        }
    }};
    text-align: center;
    padding: 14px 21px;
    color: ${({type}) => {
        switch (type){
            case 'primary':
                return '#ffffff'
            case 'outline':
                return '#5562c0'
        }
    }};
    border: ${({type}) => {
        switch (type){
            case 'primary':
                return 'none'
            case 'outline':
                return '1px solid #5562c0'
        }
    }};
    cursor: pointer;
    font-weight: 700;
    font-size: 20px;
    line-height: 17px;
`

const TestButtons = styled.div`
    display: none;
    position: fixed;
    bottom: 5px;
    right: 15px;
    text-align: center;
    appearance: none;
`

const Controls = styled.div`
    position: fixed;
    bottom: 0;
    right: 0;
    left: 0;
    background: #121212;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 75px;
    padding: 15px;
`

const ControlButton = styled.div`
    padding: 5px;

    button {
        text-align: center;
        appearance: none;
        width: 105px;
        color: ${({type}) => {
            switch (type) {
                case 'active':
                    return '#333333'
                case 'disabled':
                    return '#999999'
                default:
                    return '#FFFFFF'
            }
        }};
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        font-size: 13px;
        padding: 5px 15px;
        cursor: pointer;
        border-radius: 10px;
        border: 0;

        background: ${({type}) => {
            switch (type) {
                case 'danger':
                    return '#ED6D5F'
                case 'active':
                    return '#EAEAEA'
                default:
                    return 'transparent'
            }
        }};

        &:hover {
            background: ${({type}) => {
                switch (type) {
                    case 'danger':
                        return 'rgba(237,109,95,0.8)'
                    case 'active':
                        return 'rgba(234,234,234,0.8)'
                    case 'disabled':
                        return 'transparent'
                    default:
                        return 'rgba(255,255,255,0.1)'
                }
            }};
        }

        img {
            display: block;
            height: 24px;
            margin-bottom: 3px;
        }
    }


    &:not(:last-child) {
        border-right: 1px solid #fff;
    }
`

const InCallWrapper = styled.div`
    display: flex;
    flex-flow: row wrap;
    gap: 0.5em;
    padding: 0.5em 1em;
`

const InCallHeader = styled.div`
    font-size: 14px;
    font-style: italic;
    flex: 1 0 100%;
`

const InCallBadge = styled.div`
    border: 1px solid white;
    border-radius: 20%/50%;
    padding: 0.25em 0.75em;
    font-weight: bold;
`

const Room = ({apiKey, sessionUuid, openTokId, token, isInstructor, autojoin = false}) => {
    const [currentPage, setCurrentPage] = useState(0)
    const connection = useRef(null);
    const [extraConnections, setExtraConnections] = useState(0)
    const [layout, setLayout] = useState('lobby')
    const [videoPublished, setVideoPublished] = useState(true)
    const [audioPublished, setAudioPublished] = useState(true)
    const [screenShared, setScreenShared] = useState(false)
    const [subscriptions, _setSubscriptions] = useState({})
    const connectionCount = useMemo(() =>
            layout === 'lobby' ? 2 : (1 + extraConnections + (subscriptions ? Object.keys(subscriptions).length : 0)),
        [extraConnections, layout, subscriptions]
    )
    const [connections, setConnections] = useState([])
    const [videoSource, setVideoSource] = useState(null)
    const [audioSource, setAudioSource] = useState(null)
    const [inputDevices, setInputDevices] = useState({video: [], audio: []})
    const [, {init, close}] = useMedia()
    const [mediaInitialized, setMediaInitialized] = useState(false)
    const [spotlightSession, _setSpotlightSession] = useState(null)
    const [screenShareSubscribed, setScreenShareSubscribed] = useState(false)
    // PiP (FaceTime-style) applies only to a plain 1-on-1: exactly one remote camera,
    // no screenshare in either direction, past the lobby, and no extra connections.
    // It is a presentation of existing state — pin/force-pin still work through
    // spotlightSession/forcedSpotlight; the pinned stream is simply the big tile.
    const pipEligible = useMemo(() => {
        const subs = subscriptions ? Object.values(subscriptions) : []
        return layout !== 'lobby'
            && !screenShared
            && !screenShareSubscribed
            && extraConnections === 0
            && subs.length === 1
            && subs[0].subscriber.stream.videoType !== 'screen'
    }, [layout, screenShared, screenShareSubscribed, extraConnections, subscriptions])
    const joined = useRef(false)
    const waitingForApproval = useRef(false);
    const subscribersRef = useRef(subscriptions)
    const mainPublisherRef = useRef('')
    const spotlightRef = useRef(null)
    const setSubscriptions = data => {
        subscribersRef.current = data
        _setSubscriptions(data)
    }
    const setSpotlightSession = data => {
        spotlightRef.current = data
        _setSpotlightSession(data)
    }
    const [forcedSpotlight, _setForcedSpotlight] = useState(null)
    const forcedSpotlightRef = useRef(null)
    const forcedPinnerRef = useRef(null)
    const setForcedSpotlight = data => {
        forcedSpotlightRef.current = data
        _setForcedSpotlight(data)
    }
    const [approved, setApproved] = useState(false)
    const [pendingStreams, _setPendingStreams] = useState([])
    const pendingStreamsRef = useRef([])
    const setPendingStreams = data => {
        pendingStreamsRef.current = data
        _setPendingStreams(data)
    }
    const [mirrored, setMirrored] = useState(true)
    const grantedIds = useRef([])

    const [settingsModal, setSettingsModal] = useState({
        open: false,
        video: null,
        audio: null
    })

    const [gateModal, setGateModal] = useState({
        open: false,
        heading: '',
        action: false,
        session: null,
        id: null
    })


    const session = useSession({
        apiKey,
        sessionId: openTokId,
        token,
        onSessionConnected: (e) => {
            connection.current = e.target.connection
        },
        onSessionDisconnected: () => {
            new Audio(exitChime).play();
        }
    })

    const getDevices = () => {
        OT.getDevices((error, devices) => {
            if (!error) {
                const video = devices
                    .filter(device => device.kind === 'videoInput')
                    .map(v => v.deviceId === 'default' ? {...v, label: `Default Device [${v.label}]`} : v)
                const audio = devices
                    .filter(device => device.kind === 'audioInput')
                    .map(a => a.deviceId === 'default' ? {...a, label: `Default Device [${a.label}]`} : a)
                setInputDevices({
                    video,
                    audio
                })

                setVideoSource(video[0].deviceId)
                setAudioSource(audio[0].deviceId)
            }
        })
    }

    const initialize = async () => {
        await init({})
        navigator.mediaDevices.addEventListener('devicechange', () => getDevices())
        setMediaInitialized(true)
    }

    useEffect(() => {
        (() => initialize())(); //iife, for reasons
        console.log({apiKey, sessionId: openTokId, sessionUuid, token, isInstructor, autojoin})
        return close
    }, [])

    useEffect(() => {
        getDevices()
    }, [mediaInitialized])

    const videoElementCreated = (event, subscriber, data, stream) => {
        setSubscriptions({
            ...subscribersRef.current,
            [subscriber.streamId]: {
                subscriber,
                videoElement: event.element,
                ...data
            }
        })

        if (stream.videoType === 'screen' && joined.current) {
            setSpotlightSession(stream.streamId)
            setScreenShareSubscribed(true)
            setLayout('spotlight')
        }
    }

    const subscribe = ({stream, data}) => {
        const subscriber = session.subscribe(stream, null, {
            insertDefaultUI: false,
            preferredResolution: "1280x720",
            showControls: false,
            subscribeToAudio: true,
            subscribeToVideo: true
        })

        subscriber.on('videoElementCreated', (e) => videoElementCreated(e, subscriber, data, stream))
    }

    const grantAccess = ({session, id}) => {
        grantedIds.current = [...grantedIds.current, id]
        session.signal({
            type: 'grantAccess',
            data: id
        })
        setGateModal({...gateModal, open: false, id: null, heading: ''})
    }

    const requestAccess = ({session, data}) => {
        session.signal({
            type: 'requestAccess',
            data: data
        })
    }

    const streamCreated = (e) => {
        const data = Object.fromEntries(new URLSearchParams(e.stream.connection.data))
        if (e.stream.videoType !== 'screen') {
            setConnections(c => [
                ...c, {
                    ...e.stream.connection,
                    ...data
                }
            ])
        }

        if (joined.current) {
            subscribe({stream: e.stream, data})
            if(e.stream.videoType !== 'screen' && grantedIds.current.indexOf(e.stream.connection.id) === -1) {
                new Audio(entranceChime).play();
            }
        } else {
            if (waitingForApproval.current) {
                requestAccess({session, data: session.connection.data})
            }
            setPendingStreams([...pendingStreamsRef.current, {stream: e.stream, data}])
        }

        // Lock late joiners to an active teacher pin (targeted so others don't re-flash).
        if (isModerator() && forcedSpotlightRef.current) {
            session.signal({type: 'forcePin', data: forcedSpotlightRef.current, to: e.stream.connection})
        }
    }

    const streamDestroyed = (e) => {
        if (e.stream.id === forcedSpotlightRef.current) {
            // The pinned student left — release everyone so no one is stuck locked.
            forcedPinnerRef.current = null
            setForcedSpotlight(null)
            if (isModerator()) {
                session.signal({type: 'forceUnpin'})
            }
        }
        if (e.stream.videoType === 'screen' || e.stream.id === spotlightRef.current) {
            setSpotlightSession(null)
            setLayout('grid')
            setScreenShareSubscribed(false)
        } else {
            new Audio(exitChime).play();
        }
        setConnections(c => c.filter(co => co.id !== e.stream.connection.id))
        setSubscriptions((({[e.stream.id]: _, ...o}) => o)(subscribersRef.current))
    }

    const connectionDestroyed = (e) => {
        // If the teacher who set the pin disconnects, release everyone.
        if (forcedSpotlightRef.current && e.connection && e.connection.id === forcedPinnerRef.current) {
            forcedPinnerRef.current = null
            setForcedSpotlight(null)
            setLayout('grid')
        }
    }

    const isModerator = () => (isInstructor || session?.connection?.permissions.forceDisconnect === 1)

    const signal = ({type, data, from}) => {
        switch (type) {
            case 'signal:requestAccess':
                const {name} = Object.fromEntries(new URLSearchParams(data))
                if (isModerator() && joined.current) {
                    new Audio(entranceChime).play();
                    setGateModal({...gateModal, heading: `Allow ${name} to join the session?`, open: true, action: true, session, id: from.id})
                }
                break;
            case 'signal:grantAccess':
                if (data === connection.current.id && waitingForApproval.current) {
                    waitingForApproval.current = false
                    setApproved(true)
                    setGateModal({...gateModal, open: false})
                }

                if(isModerator()) {
                    setGateModal({...gateModal, heading: '', open: false, action: false, session: null, id: null})
                }
                break;
            case 'signal:forcePin':
                forcedPinnerRef.current = from ? from.id : null
                setForcedSpotlight(data)
                setLayout('spotlight')
                break;
            case 'signal:forceUnpin':
                forcedPinnerRef.current = null
                setForcedSpotlight(null)
                setLayout('grid')
                break;
        }
    }

    useEffect(() => {
        if (approved) {
            join()
            setApproved(false)
        }
    }, [approved])

    Echo.channel(`session.${sessionUuid}`)
        .listen('StartCountdown', (e) => {
            if(autojoin && !joined.current) {
                join(true)
            }
        })
        .listen('StopRecording', (e) => {
            if(autojoin){
                window.location.href = '/'
            }
        })

    const layoutRef = useRef(null)
    const mainCameraRef = useRef(null)
    const mainCameraPublisher = usePublisher({
        containerRef: mainCameraRef,
        videoContentHint: 'motion',
        videoSource,
        audioSource,
        mirror: mirrored
    })
    mainPublisherRef.current = mainCameraPublisher ? mainCameraPublisher.streamId : ''
    const screenShareRef = useRef(null)
    const [screenSharePublisher, setScreenSharePublisher] = useState(null)

    useEffect(() => {
        if (session) {
            session.on({
                streamCreated,
                streamDestroyed,
                connectionDestroyed,
                signal
            })
        }
    }, [session])

    const incrementPage = () => setCurrentPage(currentPage + 1)
    const decrementPage = () => setCurrentPage(currentPage - 1)

    const join = useCallback(
        (silent = false) => {
            if (session && connection.current && mainCameraPublisher && !joined.current) {
                session.publish(mainCameraPublisher)
                pendingStreams.forEach(s => subscribe(s))
                setPendingStreams([]);
                joined.current = true
                const ss = Object.values(subscribersRef.current).find(s => s.subscriber.stream.videoType === 'screen')
                if (ss) {
                    setSpotlightSession(ss.subscriber.stream.id)
                    setLayout('spotlight')
                    setScreenShareSubscribed(true)
                } else {
                    setLayout('grid')
                }

                if(!silent) {
                    new Audio(entranceChime).play();
                }
            }
        },
        [session, mainCameraPublisher, pendingStreams],
    )

    useEffect(
        () => {
            if (screenShared && session && connection && screenShareRef && !screenSharePublisher) {
                const pub = OT.initPublisher(screenShareRef.current, {
                    videoSource: 'screen',
                    showControls: false,
                    height: null,
                    width: null,
                    fitMode: 'contain'
                })
                if (pub) {
                    pub.on('mediaStopped', function () {
                        setScreenSharePublisher(null)
                        setSpotlightSession(null)
                        setLayout('grid')
                        setScreenShared(false)
                    })

                    pub.on('accessDenied', function() {
                        console.group('accessDenied')
                        console.log(pub)
                        console.groupEnd()
                        setSpotlightSession(null)
                        setLayout('grid')
                        setScreenShared(false)
                    })

                    pub.on('accessAllowed', function() {
                        console.group('accessAllowed')
                        console.log(pub)
                        console.groupEnd()
                        setScreenSharePublisher(pub)
                        session.publish(pub)
                        setSpotlightSession('screen')
                        setLayout('spotlight')
                    })
                }
            } else if (!screenShared && session && connection && screenSharePublisher) {
                session.unpublish(screenSharePublisher)
                screenSharePublisher.destroy()
                setScreenSharePublisher(null)
                setLayout('grid')
            }
        },
        [session, connection, screenShareRef, screenShared]
    )

    useEffect(() => {
        if (mainCameraPublisher) {
            mainCameraPublisher.publishVideo(videoPublished)
        }
    }, [videoPublished])

    useEffect(() => {
        if (mainCameraPublisher) {
            mainCameraPublisher.publishAudio(audioPublished)
        }
    }, [audioPublished])

    useEffect(() => {
        if(autojoin) {
            join(true)
        }
    }, [videoPublished, audioPublished, autojoin, session, mainCameraPublisher])

    const [resizeListener, resizeSizes] = useResizeAware()
    const [sizes, setSizes] = useState(null)
    const roomRef = useRef(null)

    useEffect(() => {
        if (resizeSizes) {
            setSizes(resizeSizes)
        }
    }, [resizeSizes]);

    useLayoutEffect(() => {
        setSizes({width: roomRef.current.offsetWidth, height: roomRef.current.offsetHeight})
    }, [])

    const [testExtras, setTestExtras] = useState(0)

    const screenSharableStatus = useMemo(() => {
        if (screenShared) {
            return 'active'
        }

        if (screenShareSubscribed && session.capabilities.forceDisconnect !== 1) {
            return 'disabled'
        }

        return ''
    }, [screenShared, screenShareSubscribed, session])

    const shareScreen = () => {
        if (screenShareSubscribed && session.capabilities.forceUnpublish === 1) {
            session.forceUnpublish(subscribersRef.current[spotlightRef.current].subscriber.stream)
            setScreenShared(true)
            setScreenShareSubscribed(false)
            setSpotlightSession('screen')
            setLayout('spotlight')
        } else if (!screenShareSubscribed) {
            setScreenShared(true)
        }
    }

    const connectionList = useMemo(() => connections
            .filter(c => !!connection && c.id !== connection?.current?.id)
            .sort((a, b) => a.name - b.name),
        [connections, connection]
    );

    const attemptJoin = useCallback(() => {
        if (!session) {
            return;
        }
        if (isModerator()) {
            join()
            return;
        }

        waitingForApproval.current = true
        setGateModal({...gateModal, open: true, heading: `Requesting access to lesson...` })
        requestAccess({session, data: connection.current.data})
    }, [session, connection, isModerator])

    const changeSpotlight = (uuid) => {
        // Teacher (moderator) clicks force-pin the whole room and are broadcast.
        if (isModerator()) {
            if (forcedSpotlightRef.current === uuid) {
                // Clicking the already-pinned student again releases everyone.
                session.signal({type: 'forceUnpin'})
                setForcedSpotlight(null)
                setLayout('grid')
            } else {
                session.signal({type: 'forcePin', data: uuid})
                setForcedSpotlight(uuid)
                setLayout('spotlight')
            }
            return
        }

        // Non-moderators cannot change their view while the teacher has pinned someone.
        if (forcedSpotlightRef.current) {
            return
        }

        if(spotlightRef.current && (spotlightRef.current === uuid || uuid === null || (spotlightRef.current === 'mainPublisher' && uuid === mainPublisherRef.current))){
            setLayout('grid')
            setSpotlightSession(false)
        } else if (spotlightRef.current !== 'mainPublisher' && uuid === mainPublisherRef.current) {
            setLayout('spotlight')
            setSpotlightSession('mainPublisher')
        } else {
            setLayout('spotlight')
            setSpotlightSession(uuid)
        }
    }


    // A participant's own camera exists on their client as the PUBLISHER, never as a
    // subscriber, so a spotlight target that matches our own stream id has no tile in
    // `subscriptions` to render. Map it onto the publisher tile ('mainPublisher') — the
    // same token the local self-pin path in changeSpotlight() already uses. Without this
    // the spotlight layout renders with no spotlighted tile at all (empty canvas + the
    // narrow side column of small tiles).
    const resolveSpotlightTarget = (target) => (
        target && mainPublisherRef.current && target === mainPublisherRef.current
            ? 'mainPublisher'
            : target
    )

    // Pinning only means something once there is another camera on screen to pin.
    const hasRemoteCamera = !!subscriptions && Object.keys(subscriptions).some(
        k => subscriptions[k].subscriber.stream.videoType !== 'screen'
    )

    return (
        <RoomWrapper id="room" ref={roomRef}>
            {resizeListener}
            <PinTip show={layout !== 'lobby' && hasRemoteCamera} moderator={isModerator()} />
            {sizes && (
                <Layout
                    layoutStyle={pipEligible ? 'pip' : (forcedSpotlight ? 'spotlight' : layout)}
                    sizes={sizes}
                    connectionCount={connectionCount}
                    subscriptions={subscriptions}
                    extraConnections={extraConnections}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    mainCameraRef={mainCameraRef}
                    mainCameraMirrored={mirrored}
                    screenShareRef={screenShareRef}
                    audioPublished={audioPublished}
                    videoPublished={videoPublished}
                    screenShared={screenShared}
                    ref={layoutRef}
                    spotlightSession={resolveSpotlightTarget(forcedSpotlight ? forcedSpotlight : spotlightSession)}
                    forcedSpotlight={forcedSpotlight}
                    activeScreenshareSubscription={screenShareSubscribed}
                    setSpotlight={changeSpotlight}
                    moderator={isModerator()}
                    mainPublisherStreamId={mainPublisherRef.current}
                    Lobby={() => (
                        <>
                            <DeviceSettingsDropdown
                                icon={<FontAwesomeIcon icon={light('video')} size={'lg'} fixedWidth />}
                                srLabel={'Camera'}
                                options={inputDevices.video.reduce(
                                    (list, curr) => [...list, {value: curr.deviceId, label: curr.label}], [])}
                                value={videoSource}
                                onChange={e => setVideoSource(e.target.value)}
                                id={'camera-selector'}
                            />
                            <DeviceSettingsDropdown
                                icon={<FontAwesomeIcon icon={light('microphone')} size={'lg'} fixedWidth />}
                                srLabel={'Microphone'}
                                options={inputDevices.audio.reduce(
                                    (list, curr) => [...list, {value: curr.deviceId, label: curr.label}], [])}
                                value={audioSource}
                                onChange={e => setAudioSource(e.target.value)}
                                id={'audio-selector'}
                            />
                            <CallButtonGroup style={{alignSelf: 'center'}}>
                                <CallButton type={audioPublished ? 'hollow' : 'danger'} round={true}
                                            onClick={() => setAudioPublished(!audioPublished)}>
                                    <span hidden={!audioPublished}>
                                        <FontAwesomeIcon icon={light('microphone')} size={'2xl'} transform={'shrink-2'} fixedWidth />
                                    </span>
                                    <span hidden={audioPublished}>
                                        <FontAwesomeIcon icon={solid('microphone-slash')} size={'2xl'} transform={'shrink-2'} fixedWidth />
                                    </span>
                                </CallButton>
                                <CallButton type={videoPublished ? 'hollow' : 'danger'} round={true}
                                            onClick={() => setVideoPublished(!videoPublished)}>
                                    <span hidden={!videoPublished}>
                                        <FontAwesomeIcon icon={light('video')} size={'2xl'} transform={'shrink-2'} fixedWidth />
                                    </span>
                                    <span hidden={videoPublished}>
                                        <FontAwesomeIcon icon={solid('video-slash')} size={'2xl'} transform={'shrink-2'} fixedWidth />
                                    </span>
                                </CallButton>
                                <CallButton type={mirrored ? 'active' : 'hollow'} round={true}
                                            onClick={() => setMirrored((m) => !m)}
                                >
                                    <FontAwesomeIcon icon={light('camera-rotate')} size={'2xl'} transform={'shrink-2'} fixedWidth />
                                </CallButton>
                                <CallButton type='primary' onClick={attemptJoin}>
                                    Join Call
                                </CallButton>
                            </CallButtonGroup>
                            <InCallWrapper>
                                <InCallHeader>
                                    In Call:
                                </InCallHeader>
                                {connectionList.map(c => (<InCallBadge key={c.id}>{c.name}</InCallBadge>))}
                                {!(connectionList?.length) && (<em>No One's Here Yet</em>)}
                            </InCallWrapper>
                        </>
                    )}
                />
            )}
            {layout !== 'lobby' && (
                <Controls>
                    <ControlButton
                        type={(!audioPublished) ? 'danger' : ''}
                        onClick={() => setAudioPublished(!audioPublished)}
                    >
                        {audioPublished ? (
                            <button onClick={() => setAudioPublished(false)}>
                                <FontAwesomeIcon icon={light('microphone')} size={'2xl'}/>
                                Mute
                            </button>
                        ) : (
                            <button onClick={() => setAudioPublished(true)}>
                                <FontAwesomeIcon icon={light('microphone-slash')} size={'2xl'}/>
                                Unmute
                            </button>
                        )}
                    </ControlButton>
                    <ControlButton
                        type={(!videoPublished) ? 'danger' : ''}
                    >
                        {videoPublished ? (
                            <button onClick={() => setVideoPublished(false)}>
                                <FontAwesomeIcon icon={light('video')} size={'2xl'}/>
                                Hide Video
                            </button>
                        ) : (
                            <button onClick={() => setVideoPublished(true)}>
                                <FontAwesomeIcon icon={light('video-slash')} size={'2xl'}/>
                                Show Video
                            </button>
                        )}
                    </ControlButton>
                    <ControlButton
                        type={screenSharableStatus}
                        disabled={screenSharableStatus === 'disabled'}
                    >
                        {screenShared ? (
                            <button onClick={() => setScreenShared(false)}>
                                <FontAwesomeIcon icon={light('screencast')} size={'2xl'}/>
                                Stop Sharing
                            </button>
                        ) : (
                            <button onClick={() => shareScreen()}>
                                <FontAwesomeIcon icon={light('screencast')} size={'2xl'}/>
                                Screenshare
                            </button>
                        )}
                    </ControlButton>
                    <ControlButton>
                        <button onClick={() => setSettingsModal({...settingsModal, open: true})}>
                            <FontAwesomeIcon icon={regular('ellipsis-vertical')} size={'2xl'}/>
                            Settings
                        </button>
                    </ControlButton>
                </Controls>
            )}


            <TestButtons>
                <input type='number' value={testExtras}
                       onChange={e => setTestExtras(Number.parseInt(e.target.value))}/>
                <button onClick={() => setExtraConnections(testExtras)}>Set</button>
                <button onClick={() => setExtraConnections(extraConnections + 1)}>Inc</button>
                <button onClick={() => setExtraConnections(extraConnections - 1)}>Dec</button>
                <button onClick={decrementPage}>&lt;</button>
                <button onClick={incrementPage}>&gt;</button>
                <button onClick={() => setVideoPublished(!videoPublished)}>Video Toggle</button>
                <button onClick={() => setAudioPublished(!audioPublished)}>Audio Toggle</button>
            </TestButtons>
            <ModalWrapper open={settingsModal.open}>
                <SettingsModalContainer>
                    <SettingsModalHeader>Device Settings</SettingsModalHeader>
                    <DeviceSettingsDropdown
                        icon={<FontAwesomeIcon icon={light('video')} size={'lg'} fixedWidth />}
                        srLabel={'Camera'}
                        options={inputDevices.video.reduce(
                            (list, curr) => [...list, {value: curr.deviceId, label: curr.label}], [])}
                        value={settingsModal.video || videoSource}
                        onChange={e => setSettingsModal({...settingsModal, video: e.target.value})}
                        id={'camera-selector'}
                    />
                    <DeviceSettingsDropdown
                        icon={<FontAwesomeIcon icon={light('microphone')} size={'lg'} fixedWidth />}
                        srLabel={'Microphone'}
                        options={inputDevices.audio.reduce(
                            (list, curr) => [...list, {value: curr.deviceId, label: curr.label}], [])}
                        value={settingsModal.audio || audioSource}
                        onChange={e => setSettingsModal({...settingsModal, audio: e.target.value})}
                        id={'audio-selector'}
                    />
                    <SettingsModalButtonWrapper>
                        <SettingsModalButton fullwidth={true} active={mirrored} onClick={() => {
                            setMirrored(!mirrored)
                        }}>
                            <SettingsModalButtonIcon>
                                <FontAwesomeIcon icon={light('camera-rotate')} size={'lg'} fixedWidth />
                            </SettingsModalButtonIcon>
                            Mirror Camera
                        </SettingsModalButton>
                    </SettingsModalButtonWrapper>
                    <SettingsModalDivider></SettingsModalDivider>
                    <SettingsModalButtonWrapper>
                        <SettingsModalButton onClick={() => {
                            setAudioSource(settingsModal.audio)
                            setVideoSource(settingsModal.video)
                            setSettingsModal({open: false, video: null, audio: null})
                        }}>
                            Close
                        </SettingsModalButton>
                    </SettingsModalButtonWrapper>
                </SettingsModalContainer>
            </ModalWrapper>
            <ModalWrapper open={gateModal.open}>
                <GateModalContainer>
                    <GateModalHeading>{gateModal.heading}</GateModalHeading>
                    {gateModal.action && gateModal.session && gateModal.id && (
                        <GateModalActionWrapper>
                            <GateModalAction type={`primary`} onClick={() => grantAccess(gateModal)}>
                                Allow
                            </GateModalAction>

                            <GateModalAction type={`outline`} onClick={() => setGateModal({...gateModal, open: false, action: false, id: null})}>
                                Cancel
                            </GateModalAction>
                        </GateModalActionWrapper>
                    )}
                </GateModalContainer>
            </ModalWrapper>
        </RoomWrapper>
    )
}

export default Room
